import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import type { CreateLearnTopicPayload, LearnIcon, LearnKind, LearnSurface, LearnTopic } from '@/client/types/learn';
import {
  useCreateLearnTopicMutation,
  useUpdateLearnTopicMutation,
} from '@/client/api/learn';
import { LearnIconPicker } from './LearnIconPicker';

interface TopicFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topic?: LearnTopic | null;
  onSuccess?: (topic: LearnTopic) => void;
}

export const TopicFormDialog: React.FC<TopicFormDialogProps> = ({
  open,
  onOpenChange,
  topic,
  onSuccess,
}) => {
  const isEdit = Boolean(topic);
  const createMutation = useCreateLearnTopicMutation();
  const updateMutation = useUpdateLearnTopicMutation();

  const [title, setTitle] = useState('');
  const [slugId, setSlugId] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<LearnIcon>('compass');
  const [kind, setKind] = useState<LearnKind>('open');
  const [surface, setSurface] = useState<LearnSurface>('reference');
  const [published, setPublished] = useState(false);

  useEffect(() => {
    if (topic && open) {
      setTitle(topic.title || '');
      setSlugId(topic.id || '');
      setDescription(topic.description || '');
      setIcon((topic.icon as LearnIcon) || 'compass');
      setKind(topic.kind || 'open');
      setSurface(topic.surface || 'reference');
      setPublished(topic.published ?? false);
    } else if (open) {
      setTitle('');
      setSlugId('');
      setDescription('');
      setIcon('compass');
      setKind('open');
      setSurface('reference');
      setPublished(false);
    }
  }, [topic, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      if (isEdit && topic) {
        const res = await updateMutation.mutateAsync({
          id: topic.id,
          title: title.trim(),
          description: description.trim() || undefined,
          icon,
          kind,
          surface,
          published,
        });
        toast.success('Topic updated successfully');
        onOpenChange(false);
        onSuccess?.(res);
      } else {
        const payload: CreateLearnTopicPayload = {
          title: title.trim(),
          id: slugId.trim() || undefined,
          description: description.trim() || undefined,
          icon,
          kind,
          surface,
          published,
        };
        const res = await createMutation.mutateAsync(payload);
        toast.success('Topic created successfully');
        onOpenChange(false);
        onSuccess?.(res);
      }
    } catch (err: any) {
      toast.error(err?.message || (isEdit ? 'Failed to update topic' : 'Failed to create topic'));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Learn Topic' : 'Create New Learn Topic'}</DialogTitle>
          <DialogDescription>
            Topics organize sections into public knowledge lanes on /learn.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="topic-title">Title *</Label>
            <Input
              id="topic-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              placeholder="e.g. Selling & listings"
              required
            />
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="topic-slug">Topic ID / Slug (optional)</Label>
              <Input
                id="topic-slug"
                value={slugId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlugId(e.target.value)}
                placeholder="e.g. selling (defaults from title)"
              />
              <p className="text-[11px] text-gray-500">
                Determines URL path: /learn?topic={slugId || 'slug'}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="topic-desc">Description</Label>
            <textarea
              id="topic-desc"
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder="Brief summary of what readers will learn in this topic..."
              rows={2}
              className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <LearnIconPicker value={icon} onChange={setIcon} />
            </div>

            <div className="space-y-1.5">
              <Label>Surface Type</Label>
              <select
                value={surface}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSurface(e.target.value as LearnSurface)}
                className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="reference">Reference (Knowledge Base)</option>
                <option value="path">Path (Ordered Course)</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Visibility Kind</Label>
            <select
              value={kind}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setKind(e.target.value as LearnKind)}
              className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="open">Open (Publicly visible)</option>
              <option value="monitored">Monitored (Hidden / Verification path)</option>
            </select>
            {kind === 'monitored' && (
              <div className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-xs text-amber-800 border border-amber-200">
                <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                <span>
                  <strong>Notice:</strong> Monitored topics are hidden from the public /learn catalog.
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 bg-gray-50">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Publish State</Label>
              <p className="text-xs text-gray-500">
                {published
                  ? 'Topic and its sections are live when kind is Open.'
                  : 'Draft topic. Hidden from public readers.'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEdit ? 'Save changes' : 'Create topic'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
